export type TaxonomyCategory = 'skinType' | 'skinConcern' | 'efficacy';

export const TAXONOMY_CATEGORY_LABELS: Record<TaxonomyCategory, string> = {
  skinType: 'Skin Type & Condition',
  skinConcern: 'Skin Concern',
  efficacy: 'Efficacy',
};

export interface TaxonomyDescription {
  descriptions: string[];
}

export const EFFICACY_DESCRIPTION: Record<string, TaxonomyDescription> = {
  Hydrating: {
    descriptions: [
      'Products that deeply moisturize and lock in hydration.',
      'Ideal for dry skin or when your skin needs extra moisture.',
      'Look for ingredients like hyaluronic acid, glycerin, or ceramides.',
    ],
  },
  Brightening: {
    descriptions: [
      'Formulations that help even skin tone and reduce dark spots.',
      'Targets hyperpigmentation and dullness for a radiant glow.',
      'Common ingredients include vitamin C, niacinamide, or alpha arbutin.',
    ],
  },
  'Anti-aging': {
    descriptions: [
      'Products designed to reduce fine lines and improve skin texture.',
      'Helps maintain skin elasticity and firmness over time.',
      'Look for retinol, peptides, or antioxidants like vitamin E.',
    ],
  },
  Soothing: {
    descriptions: [
      'Gentle formulations that calm irritated or sensitive skin.',
      'Reduces redness and inflammation while providing comfort.',
      'Contains ingredients like aloe vera, centella, or panthenol.',
    ],
  },
  Firming: {
    descriptions: [
      'Formulations that improve elasticity and skin resilience.',
      'Targets sagging or loss of firmness for a more lifted appearance.',
      'Common ingredients include peptides, collagen, or adenosine.',
    ],
  },
  Wrinkles: {
    descriptions: [
      'Formulations that smooth fine lines and reduce visible signs of aging.',
      'Supports collagen production and skin renewal.',
      'Common ingredients include retinol, peptides, or bakuchiol.',
    ],
  },
  'Even-toning': {
    descriptions: [
      'Formulations that promote a balanced complexion and refined texture.',
      'Targets uneven tone caused by UV damage or post-acne marks.',
      'Common ingredients include niacinamide, tranexamic acid, or licorice extract.',
    ],
  },
  Exfoliating: {
    descriptions: [
      'Formulations that remove dead skin cells for smoother, clearer skin.',
      'Promotes cell turnover and improves absorption of other products.',
      'Common ingredients include AHA, BHA, or PHA.',
    ],
  },
  Pigmentation: {
    descriptions: [
      'Formulations that fade dark spots and inhibit melanin production.',
      'Targets discoloration caused by sun exposure, acne, or hormones.',
      'Common ingredients include tranexamic acid, kojic acid, or arbutin.',
    ],
  },
  'Scar-reducing': {
    descriptions: [
      'Formulations that help smooth and repair uneven skin texture.',
      'Supports healing and renewal of damaged skin tissue.',
      'Common ingredients include centella asiatica, madecassoside, or vitamin E.',
    ],
  },
  Redness: {
    descriptions: [
      'Formulations that calm irritation and soothe visible redness.',
      'Strengthens the skin barrier to reduce sensitivity over time.',
      'Common ingredients include green tea, cica, or panthenol.',
    ],
  },
  Acne: {
    descriptions: [
      'Formulations that target excess oil, clogged pores, and inflammation.',
      'Prevents breakouts while maintaining skin balance.',
      'Common ingredients include salicylic acid, tea tree, or zinc PCA.',
    ],
  },
  'Dark Circle': {
    descriptions: [
      'Formulations that brighten and smooth the under-eye area.',
      'Targets pigmentation, puffiness, and tired appearance.',
      'Common ingredients include caffeine, vitamin C, or niacinamide.',
    ],
  },
  Blackheads: {
    descriptions: [
      'Formulations that clear and unclog pores to prevent blackhead buildup.',
      'Dissolves excess sebum and dead skin trapped within pores.',
      'Common ingredients include salicylic acid, clay, or charcoal.',
    ],
  },
  Sebum: {
    descriptions: [
      'Formulations that control excess oil and balance skin production.',
      'Helps reduce shine without stripping moisture.',
      'Common ingredients include zinc, green tea extract, or niacinamide.',
    ],
  },
  Pore: {
    descriptions: [
      'Formulations that refine and minimize the appearance of enlarged pores.',
      'Helps improve texture and prevent clogging.',
      'Common ingredients include niacinamide, witch hazel, or clay.',
    ],
  },
  Detoxifying: {
    descriptions: [
      'Formulations that draw out impurities and pollutants from the skin.',
      'Helps restore clarity and balance to congested complexions.',
      'Common ingredients include charcoal, kaolin clay, or volcanic ash.',
    ],
  },
  Moisturizing: {
    descriptions: [
      'Formulations that lock in hydration and reinforce the skin barrier.',
      'Provide lasting comfort and prevent moisture loss.',
      'Common ingredients include ceramides, shea butter, or squalane.',
    ],
  },
  'Fast-Absorbing': {
    descriptions: [
      'Formulations that quickly sink into the skin without residue.',
      'Provide hydration and nutrients with a lightweight finish.',
      'Common ingredients include water-based humectants and micro-molecule hyaluronic acid.',
    ],
  },
  'Sun Damage': {
    descriptions: [
      'Formulations that repair and protect against UV-induced damage.',
      'Target discoloration, roughness, and premature aging from sun exposure.',
      'Common ingredients include antioxidants, niacinamide, or DNA-repair enzymes.',
    ],
  },
};

export const SKIN_TYPE_DESCRIPTION: Record<string, TaxonomyDescription> = {
  'Dry Skin': {
    descriptions: [
      'Your skin often feels tight, flaky, or rough—especially after cleansing.',
      'You rarely get oily, even by the end of the day.',
      'Moisturizer seems to disappear quickly into your skin.',
    ],
  },
  'Oily Skin': {
    descriptions: [
      'Your face looks shiny, especially around your T-zone (forehead, nose, chin).',
      'You notice excess oil on tissue or blotting paper within a few hours.',
      'Pores look larger and you’re more prone to blackheads or breakouts.',
    ],
  },
  'Combination Skin': {
    descriptions: [
      'Your T-zone feels oily, but your cheeks or jawline feel dry or tight.',
      'You deal with both breakouts and dry patches—depending on the area.',
      'It’s hard to find one product that works for your whole face.',
    ],
  },
  'Normal Skin': {
    descriptions: [
      'Your skin feels balanced—not too oily, not too dry.',
      'You rarely experience breakouts, flakiness, or tightness.',
      'Most products work fine for you without causing irritation. Your skin feels smooth, soft, and looks even-toned most of the time.',
    ],
  },
  'Sensitive Skin': {
    descriptions: [
      'Your skin often feels tight, itchy, or easily irritated after cleansing or product use.',
      'You notice redness or stinging when exposed to certain ingredients, weather, or stress.',
      'Your skin barrier is delicate, and you’re more prone to reactions or flare-ups.',
    ],
  },
};

export const SKIN_CONCERN_DESCRIPTION: Record<string, TaxonomyDescription> = {
  Acne: {
    descriptions: [
      'You experience frequent breakouts, blackheads, or whiteheads.',
      'Your skin is prone to inflammation and redness around blemishes.',
      'You may have oily skin that contributes to clogged pores.',
    ],
  },
  Aging: {
    descriptions: [
      'You notice fine lines, wrinkles, or loss of firmness.',
      'Your skin may appear dull or show signs of sun damage.',
      'You’re looking to prevent further signs of aging.',
    ],
  },
  Hyperpigmentation: {
    descriptions: [
      'You have dark spots, melasma, or uneven skin tone.',
      'Sun exposure or acne scars have left marks on your skin.',
      'You’re seeking products to brighten and even your',
    ],
  },
  Sensitivity: {
    descriptions: [
      'Your skin reacts easily to products, weather, or stress.',
      'You experience redness, burning, or stinging sensations.',
      'You need gentle, fragrance-free formulations.',
    ],
  },
  Dullness: {
    descriptions: [
      'Your skin looks tired, lacking its natural glow or radiance.',
      'Stress, pollution, or lack of sleep may have left your complexion looking flat.',
      'You’re looking for products to restore brightness and a healthy, luminous look.',
    ],
  },
  Sagging: {
    descriptions: [
      'Your skin feels less firm and lacks youthful elasticity.',
      'Aging or lifestyle factors have caused a loss of definition, especially around the jawline or cheeks.',
      'You’re seeking skincare that lifts, tightens, and restores bounce to your skin.',
    ],
  },
  Wrinkles: {
    descriptions: [
      'You notice fine lines or deeper creases on your forehead, around your eyes, or mouth.',
      'Sun exposure or natural aging has reduced skin’s smoothness and elasticity.',
      'You’re looking for anti-aging products that smooth, plump, and soften your skin’s texture.',
    ],
  },
  Unevenness: {
    descriptions: [
      'Your skin tone or texture feels irregular, with rough or patchy areas.',
      'Dead skin buildup or previous breakouts have left your skin less uniform.',
      'You’re looking to refine your skin for a more balanced, even finish.',
    ],
  },
  Scars: {
    descriptions: [
      'You have visible marks left behind from acne, blemishes, or injuries.',
      'These uneven textures or dark spots make your skin tone look inconsistent.',
      'You’re seeking products that help fade scars and promote smoother skin regeneration.',
    ],
  },
  Redness: {
    descriptions: [
      'Your skin often looks flushed, irritated, or sensitive.',
      'Factors like weather changes, harsh products, or inflammation may be triggering irritation.',
      'You’re looking for calming, soothing formulas that reduce redness and strengthen your skin barrier.',
    ],
  },
  Blemishes: {
    descriptions: [
      'You experience frequent breakouts or clogged pores.',
      'Hormones, stress, or bacteria may be causing whiteheads, blackheads, or pimples.',
      'You’re looking for gentle but effective treatments to clear and prevent future blemishes.',
    ],
  },
  'Dark Circles': {
    descriptions: [
      'The area under your eyes appears shadowed or discolored.',
      'Fatigue, genetics, or poor circulation may be causing under-eye darkness.',
      'You’re looking for eye care that brightens, hydrates, and revitalizes tired eyes.',
    ],
  },
  Blackheads: {
    descriptions: [
      'You notice small dark spots, especially around your nose, chin, or forehead.',
      'Excess oil and impurities have built up in your pores and oxidized on the surface.',
      'You’re looking for clarifying care that deeply cleanses and prevents future congestion.',
    ],
  },
  Sebum: {
    descriptions: [
      'Your skin feels greasy or shiny throughout the day.',
      'Overactive oil glands or humid climates may be disrupting your skin balance.',
      'You’re looking for lightweight formulas that control shine without drying out your skin.',
    ],
  },
  Pores: {
    descriptions: [
      'Your pores appear enlarged or visible, especially around your T-zone.',
      'Excess oil or impurities may be clogging and stretching your pores.',
      'You’re looking for products that refine skin texture and minimize the look of pores.',
    ],
  },
  Dryness: {
    descriptions: [
      'Your skin feels tight, flaky, or rough to the touch.',
      'Weather, dehydration, or harsh cleansers have stripped away essential moisture.',
      'You’re looking for deeply hydrating care to restore softness and comfort.',
    ],
  },
  'Sun Damage': {
    descriptions: [
      'You have dark spots, uneven tone, or rough patches caused by UV exposure.',
      'Years of sun exposure have left your skin looking dull or prematurely aged.',
      "You're seeking restorative care that repairs damage and protects your skin barrier.",
    ],
  },
};

export const TAXONOMY_DESCRIPTIONS: Record<TaxonomyCategory, Record<string, TaxonomyDescription>> = {
  skinType: SKIN_TYPE_DESCRIPTION,
  skinConcern: SKIN_CONCERN_DESCRIPTION,
  efficacy: EFFICACY_DESCRIPTION,
};
